/**
 * TaskSurfaceGuard — datengesteuerter CanActivate-Guard für Aufgaben-Child-Routen
 * (TASK-GAP-010, 13-CONTRACT-FREEZE-P0 §2.2).
 *
 * Liest `route.data.surfaceAction` (z. B. 'ManageRoutines'/'ViewCompliance') bzw.
 * `route.data.requireManagerView` und prüft sie gegen den serverautoritativen
 * {@link TaskScopeService}. Fehlt die Berechtigung → Weiterleitung auf die dedizierte
 * In-Surface-Seite `/tasks/permission-denied` (NICHT auf den Subscription-Redirect —
 * das ist Feature-Gating, nicht Permission-Denied).
 *
 * **Fail-closed:** Bei Scope-Fehler (Netz/500) verweigert der Guard grundsätzlich.
 */
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, UrlTree } from '@angular/router';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { TaskScopeService } from '../state/task-scope.service';
import { TaskPermissionsService } from '../services/task-permissions.service';

@Injectable({ providedIn: 'root' })
export class TaskSurfaceGuard implements CanActivate {
  constructor(
    private readonly scope: TaskScopeService,
    private readonly perms: TaskPermissionsService,
    private readonly router: Router,
  ) {}

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> {
    const action = route.data['surfaceAction'] as string | undefined;
    const requireManagerView = route.data['requireManagerView'] === true;

    return this.scope.ensureLoaded().pipe(
      map(() => {
        if (this.scope.error()) return this.deny(); // fail-closed
        if (action && this.scope.hasAction(action)) return true;
        if (requireManagerView && this.perms.canViewManagerScope()) return true;
        return this.deny();
      }),
    );
  }

  private deny(): UrlTree {
    return this.router.createUrlTree(['/tasks', 'permission-denied']);
  }
}
